import {
	ENTRY_ESTIMATE_MAX,
	ENTRY_NUMBER_MAX,
	EOI_ESTIMATE_FIELDS,
	EOI_MEDAL_FIELDS,
	type EbnEventRow,
	type EbnSportRow,
	type EoiEstimateField,
	type EoiEstimateTotals,
	type EoiEstimates,
	type EoiEventPick,
	type EoiEventRow,
	type EoiMedalField,
	type EoiSportInput,
	type EoiSportRow,
	type EventMedalCap,
	eoiSportAthletes,
	eoiSportMedals,
	eventMedalCap,
	impliedAthletes,
} from "@seagames/contracts";

/**
 * The editors' buffered state, pure. Changes stay in the page until one
 * explicit Save (every save is an audit row — no autosave); the API judges
 * every row again in its writing statement (caps included: the steppers'
 * clamps are a courtesy, `fn_eoi_event_guard` / `fn_ebn_entry_guard` decide).
 *
 * A number is `null` = "not given", distinct from 0 (0062).
 */

// ── Leaving the editor ──────────────────────────────────────────────────────

/**
 * The search params that change WHAT is being edited: another NOC (`org`) or
 * another games. The page-bar Back of an admin editing on behalf drops `org`
 * on the same path, and a NOC with several organisations switches with it —
 * so a pathname check alone let both (and browser Back) through. Filter
 * params (`ticked`, `search`, …) only change what is shown: never blocked.
 */
export const LEAVE_GUARDED_PARAMS = ["org", "games_id"] as const;

/** Leaving `current` for `next` would drop the editor's draft (`LeaveGuard`). */
export function leavesEditor(current: { pathname: string; search: unknown }, next: { pathname: string; search: unknown }): boolean {
	if (current.pathname !== next.pathname) return true;
	const a = (current.search ?? {}) as Record<string, unknown>;
	const b = (next.search ?? {}) as Record<string, unknown>;
	return LEAVE_GUARDED_PARAMS.some((k) => a[k] !== b[k]);
}

// ── EOI ─────────────────────────────────────────────────────────────────────

/** One picked event's optional numbers. */
export type EoiEventDraft = EoiEventPick;

export interface EoiDraftRow extends EoiEstimates {
	selected: boolean;
	/** The picked events (event id → its numbers). Empty = interested in ALL the sport's events. */
	events: Record<string, EoiEventDraft>;
}
export type EoiDraft = Record<string, EoiDraftRow>;

const EVENT_FIELDS = ["est_entries", ...EOI_MEDAL_FIELDS] as const;
export type EoiEventField = (typeof EVENT_FIELDS)[number];

const pickEstimates = (r: EoiEstimates): EoiEstimates =>
	Object.fromEntries(EOI_ESTIMATE_FIELDS.map((f) => [f, r[f]])) as EoiEstimates;
const pickEvent = (r: EoiEventPick): EoiEventDraft => ({
	est_entries: r.est_entries,
	expected_gold: r.expected_gold,
	expected_silver: r.expected_silver,
	expected_bronze: r.expected_bronze,
});
export const blankEventDraft = (): EoiEventDraft => ({ est_entries: null, expected_gold: null, expected_silver: null, expected_bronze: null });

/** Every event of a sport, in programme order (disciplines, then events). */
export const eoiEventsOf = (s: EoiSportRow): EoiEventRow[] => s.disciplines.flatMap((d) => d.events);

export function eoiRowFrom(s: EoiSportRow): EoiDraftRow {
	const events: Record<string, EoiEventDraft> = {};
	for (const e of eoiEventsOf(s)) if (e.selected) events[e.event_id] = pickEvent(e);
	return { selected: s.selected, ...pickEstimates(s), events };
}

export function eoiDraftFrom(sports: readonly EoiSportRow[]): EoiDraft {
	const out: EoiDraft = {};
	for (const s of sports) out[s.sport_id] = eoiRowFrom(s);
	return out;
}

/** The sport-level fields a row saves: with events picked, medals live on the events (the API stores the sport's as null). */
export function eoiSavedFields(row: EoiDraftRow): EoiEstimates {
	const out = pickEstimates(row);
	if (Object.keys(row.events).length > 0) for (const f of EOI_MEDAL_FIELDS) out[f] = null;
	return out;
}

/** A row differs from the server's: the tick, or (while ticked) a sport number, the event picks or a pick's numbers. */
export function eoiRowDirty(server: EoiSportRow, draft: EoiDraftRow | undefined): boolean {
	if (!draft) return false;
	if (draft.selected !== server.selected) return true;
	if (!draft.selected) return false;
	const mine = eoiSavedFields(draft);
	if (EOI_ESTIMATE_FIELDS.some((f) => mine[f] !== server[f])) return true;
	for (const e of eoiEventsOf(server)) {
		const d = draft.events[e.event_id];
		if (!!d !== e.selected) return true;
		if (d && EVENT_FIELDS.some((f) => d[f] !== e[f])) return true;
	}
	return false;
}

export const eoiDirtyIds = (sports: readonly EoiSportRow[], draft: EoiDraft): string[] =>
	sports.filter((s) => eoiRowDirty(s, draft[s.sport_id])).map((s) => s.sport_id);

/** Ticked on the server, unticked now: the save removes them (and their EBN rows). */
export const eoiRemovedIds = (sports: readonly EoiSportRow[], draft: EoiDraft): string[] =>
	sports.filter((s) => s.selected && draft[s.sport_id]?.selected === false).map((s) => s.sport_id);

/** The `PUT /entries/eoi` list: every ticked sport with its picks, in programme order (a full replacement). */
export function eoiBodySports(sports: readonly EoiSportRow[], draft: EoiDraft): EoiSportInput[] {
	const out: EoiSportInput[] = [];
	for (const s of sports) {
		const d = draft[s.sport_id];
		if (!d?.selected) continue;
		const events = eoiEventsOf(s)
			.filter((e) => d.events[e.event_id])
			.map((e) => ({ event_id: e.event_id, ...(d.events[e.event_id] as EoiEventDraft) }));
		out.push({ sport_id: s.sport_id, ...eoiSavedFields(d), events });
	}
	return out;
}

/** A draft row's medals as every reader counts them (`eoiSportMedals`: the picks' sum, else the sport's own). */
export function eoiRowMedals(row: EoiDraftRow): Record<EoiMedalField, number | null> {
	return eoiSportMedals({ ...eoiSavedFields(row), events: Object.values(row.events) });
}

/**
 * A draft row's athletes as every reader counts them (`eoiSportAthletes`): per
 * gender the sport's own number, else what the picked events' expected
 * entries imply. Null = nothing given for that gender.
 */
export function eoiRowAthletes(s: EoiSportRow, row: EoiDraftRow): Pick<EoiEstimates, "est_athletes_men" | "est_athletes_women"> {
	const events = eoiEventsOf(s).flatMap((e) => {
		const pick = row.events[e.event_id];
		return pick ? [{ ...e, est_entries: pick.est_entries }] : [];
	});
	return eoiSportAthletes({ est_athletes_men: row.est_athletes_men, est_athletes_women: row.est_athletes_women, events });
}

export type EoiTotals = EoiEstimateTotals & { sports: number; events: number };

/** Over the ticked sports, not given = 0 (the API's `totals` rule; athletes by `eoiRowAthletes`). */
export function eoiTotals(sports: readonly EoiSportRow[], draft: EoiDraft): EoiTotals {
	const t = { sports: 0, events: 0, ...(Object.fromEntries(EOI_ESTIMATE_FIELDS.map((f) => [f, 0])) as EoiEstimateTotals) };
	for (const s of sports) {
		const d = draft[s.sport_id];
		if (!d?.selected) continue;
		t.sports += 1;
		t.events += Object.keys(d.events).length;
		const a = eoiRowAthletes(s, d);
		t.est_athletes_men += a.est_athletes_men ?? 0;
		t.est_athletes_women += a.est_athletes_women ?? 0;
		t.est_officials += d.est_officials ?? 0;
		const m = eoiRowMedals(d);
		for (const f of EOI_MEDAL_FIELDS) t[f] += m[f] ?? 0;
	}
	return t;
}

/** A typed estimate → null (blank) or 0..max. */
export function parseNullable(raw: string, max: number, min = 0): number | null {
	const trimmed = raw.trim();
	if (trimmed === "") return null;
	const n = Number.parseInt(trimmed, 10);
	if (!Number.isFinite(n)) return null;
	return Math.max(min, Math.min(n, max));
}

/** The first number + gives a blank `NullableStepper`: 1 (or `min` when higher), capped at `max` — "—" → 1, never 0. */
export const nullableStepStart = (min: number, max: number): number => Math.min(Math.max(min, 1), max);

/** The sport-level estimates' bound. */
export const ESTIMATE_MAX = ENTRY_ESTIMATE_MAX;

/** An event pick's expected-entries bound: its quota, never above ENTRY_NUMBER_MAX. */
export const pickEntriesCap = (e: Pick<EoiEventRow, "max_entries_per_noc">): number => Math.min(e.max_entries_per_noc ?? ENTRY_NUMBER_MAX, ENTRY_NUMBER_MAX);

/** The medal cap of a pick (contracts' `eventMedalCap`, the DB guard's rule). */
export const pickMedalCap = (e: EoiEventRow, pick: EoiEventDraft): EventMedalCap => eventMedalCap(e, pick.est_entries);

/** How high one colour may go with the other two as they are: min(its own cap, the total's room). */
export function medalRoom(e: EoiEventRow, pick: EoiEventDraft, field: EoiMedalField): number {
	const cap = pickMedalCap(e, pick);
	const own = field === "expected_gold" ? cap.gold : field === "expected_silver" ? cap.silver : cap.bronze;
	const others = EOI_MEDAL_FIELDS.filter((f) => f !== field).reduce((n, f) => n + (pick[f] ?? 0), 0);
	return Math.max(0, Math.min(own, cap.total - others));
}

/** Why the cap is what it is — the sentence under the medal steppers. */
export type MedalCapReason = "none" | "entries" | "quota" | "offered";
export function medalCapReason(e: EoiEventRow, pick: EoiEventDraft): MedalCapReason {
	if (!e.is_medal_event) return "none";
	const offered = e.gold_count + e.silver_count + e.bronze_count;
	if (pick.est_entries !== null && pick.est_entries < offered) return "entries";
	if (pick.est_entries === null && e.max_entries_per_noc !== null && e.max_entries_per_noc < offered) return "quota";
	return "offered";
}

/**
 * Fit a pick's medals into its cap: each colour ≤ its own cap, and the three
 * together ≤ the total. `keep` is the field just typed — the others give way
 * first (bronze, then silver, then gold), so what the person typed wins
 * whenever it fits on its own. Returns the pick and whether anything moved.
 */
export function fitMedals(e: EoiEventRow, pick: EoiEventDraft, keep?: EoiMedalField): { pick: EoiEventDraft; clamped: boolean } {
	const cap = pickMedalCap(e, pick);
	const next = { ...pick };
	let clamped = false;
	for (const f of EOI_MEDAL_FIELDS) {
		const v = next[f];
		const c = f === "expected_gold" ? cap.gold : f === "expected_silver" ? cap.silver : cap.bronze;
		if (v !== null && v > c) {
			next[f] = c;
			clamped = true;
		}
	}
	const order: EoiMedalField[] = ["expected_bronze", "expected_silver", "expected_gold"];
	if (keep) {
		order.splice(order.indexOf(keep), 1);
		order.push(keep);
	}
	let over = EOI_MEDAL_FIELDS.reduce((n, f) => n + (next[f] ?? 0), 0) - cap.total;
	for (const f of order) {
		if (over <= 0) break;
		const v = next[f];
		if (v === null || v === 0) continue;
		const cut = Math.min(v, over);
		next[f] = v - cut;
		over -= cut;
		clamped = true;
	}
	return { pick: next, clamped };
}

/** Set one field of a pick, clamped (entries to the quota, medals into the cap). */
export function setPickField(e: EoiEventRow, pick: EoiEventDraft, field: EoiEventField, value: number | null): { pick: EoiEventDraft; clamped: boolean } {
	if (field === "est_entries") {
		const cap = pickEntriesCap(e);
		const v = value === null ? null : Math.max(1, Math.min(value, cap));
		return fitMedals(e, { ...pick, est_entries: v });
	}
	const before = value;
	const fitted = fitMedals(e, { ...pick, [field]: value === null ? null : Math.max(0, value) }, field);
	return { pick: fitted.pick, clamped: fitted.clamped || (before !== null && fitted.pick[field] !== before) };
}

export const isEoiEstimateField = (f: string): f is EoiEstimateField => (EOI_ESTIMATE_FIELDS as readonly string[]).includes(f);

// ── EBN ─────────────────────────────────────────────────────────────────────

/** Per sport, per event: the number typed (0 = not entering). Only sports touched are present. */
export type EbnDraft = Record<string, Record<string, number>>;

export const eventsOf = (s: EbnSportRow): EbnEventRow[] => s.disciplines.flatMap((d) => d.events);

/** The event's hard cap for the stepper: `max_entries_per_noc`, never above ENTRY_NUMBER_MAX. */
export const eventCap = (e: EbnEventRow): number => Math.min(e.max_entries_per_noc ?? ENTRY_NUMBER_MAX, ENTRY_NUMBER_MAX);

export function ebnValue(draft: EbnDraft, sportId: string, e: EbnEventRow): number {
	const v = draft[sportId]?.[e.event_id];
	return v ?? e.entries ?? 0;
}

/** Events of a sport whose draft differs from the server. */
export function ebnDirtyEvents(s: EbnSportRow, draft: EbnDraft): string[] {
	const d = draft[s.sport_id];
	if (!d) return [];
	return eventsOf(s)
		.filter((e) => e.event_id in d && (d[e.event_id] ?? 0) !== (e.entries ?? 0))
		.map((e) => e.event_id);
}

/** The `PUT /entries/ebn` list for one sport: every event with a number > 0 (a full replacement). */
export function ebnBodyEntries(s: EbnSportRow, draft: EbnDraft): Array<{ event_id: string; entries: number }> {
	return eventsOf(s)
		.map((e) => ({ event_id: e.event_id, entries: ebnValue(draft, s.sport_id, e) }))
		.filter((r) => r.entries > 0);
}

/** "Enter all": 1 on every active, empty event the quota allows; the sport's other numbers stay. */
export function ebnEnterAll(s: EbnSportRow, draft: EbnDraft): Record<string, number> {
	const next = { ...draft[s.sport_id] };
	for (const e of eventsOf(s)) if (e.is_active && ebnValue(draft, s.sport_id, e) === 0 && eventCap(e) > 0) next[e.event_id] = 1;
	return next;
}

/** "Clear sport": 0 on every event. */
export function ebnClearSport(s: EbnSportRow): Record<string, number> {
	return Object.fromEntries(eventsOf(s).map((e) => [e.event_id, 0]));
}

export interface EbnCounts {
	events: number;
	entered: number;
	entries: number;
	implied: number;
}

/** A sport's live counts with the draft applied: events entered, Σ numbers, implied athletes. */
export function ebnSportCounts(s: EbnSportRow, draft: EbnDraft): EbnCounts {
	let entered = 0;
	let entries = 0;
	let implied = 0;
	const all = eventsOf(s);
	for (const e of all) {
		const v = ebnValue(draft, s.sport_id, e);
		if (v <= 0) continue;
		entered += 1;
		entries += v;
		implied += impliedAthletes(e, v);
	}
	return { events: all.length, entered, entries, implied };
}

/** The most medals an EBN number can bring (contracts' `eventMedalCap` with the number as entries); null = not entering. */
export function ebnMaxMedals(e: EbnEventRow, entries: number): number | null {
	return entries > 0 ? eventMedalCap(e, entries).total : null;
}

/**
 * The sports with the draft's numbers in place of the saved ones — what
 * `ebnWarnings` (contracts, the API's own rule) reads to judge the draft
 * live, before a save.
 */
export function ebnSportsWithDraft(sports: readonly EbnSportRow[], draft: EbnDraft): EbnSportRow[] {
	return sports.map((s) =>
		draft[s.sport_id]
			? {
					...s,
					disciplines: s.disciplines.map((d) => ({
						...d,
						events: d.events.map((e) => {
							const v = ebnValue(draft, s.sport_id, e);
							return { ...e, entries: v > 0 ? v : null };
						}),
					})),
				}
			: s,
	);
}

/** A typed number → 0..cap. */
export function clampNumber(raw: string | number, cap: number): number {
	const n = typeof raw === "number" ? raw : Number.parseInt(raw, 10);
	if (!Number.isFinite(n) || n < 0) return 0;
	return Math.min(Math.trunc(n), cap);
}

/**
 * What − gives a `NullableStepper` showing `value`: one less, or blank once
 * that would drop below 1 (or `min` when higher) — 1 → "—", never 0, the
 * mirror of `nullableStepStart`.
 */
export const nullableStepDown = (value: number, min: number): number | null => (value - 1 < Math.max(min, 1) ? null : value - 1);

/**
 * "Use EOI numbers": every empty event of the sport that the EOI gave an
 * expected entries number for takes that number (capped at the quota).
 * Events with a number are left alone. Returns the sport's draft and how
 * many events it filled.
 */
export function ebnFromEoi(s: EbnSportRow, draft: EbnDraft): { values: Record<string, number>; filled: number } {
	const values = { ...draft[s.sport_id] };
	let filled = 0;
	for (const e of eventsOf(s)) {
		const est = e.eoi_event?.est_entries ?? 0;
		if (est <= 0 || ebnValue(draft, s.sport_id, e) !== 0) continue;
		const v = Math.min(est, eventCap(e));
		if (v <= 0) continue;
		values[e.event_id] = v;
		filled += 1;
	}
	return { values, filled };
}

/** `ebnFromEoi` over every sport: the draft a NOC starts its EBN from, and the events filled. */
export function ebnDraftFromEoi(sports: readonly EbnSportRow[], draft: EbnDraft): { draft: EbnDraft; filled: number } {
	const next: EbnDraft = { ...draft };
	let filled = 0;
	for (const s of sports) {
		const r = ebnFromEoi(s, draft);
		if (r.filled === 0) continue;
		next[s.sport_id] = r.values;
		filled += r.filled;
	}
	return { draft: next, filled };
}

/** An event matches the EBN search: its name, or its sport's name / code, holds every word of the query. */
export function ebnEventMatches(q: string, s: Pick<EbnSportRow, "name" | "code">, e: Pick<EbnEventRow, "name">): boolean {
	const words = q.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
	if (words.length === 0) return true;
	const hay = `${e.name} ${s.name} ${s.code}`.toLocaleLowerCase();
	return words.every((w) => hay.includes(w));
}
